/**
 * PageRangePill — 7d / 30d / 90d segmented control shown in the
 * Island actions slot on Traffic + Performance. The route owns the
 * range state and threads it into every query on the page so all
 * surfaces (KPIs, trend chart, breakdowns, perf, perf-trend) re-
 * fetch coherently when the user widens or narrows the window.
 *
 * Visual vocabulary mirrors the Audience / Connection segmented
 * controls already used on the Performance overview — same border,
 * same paper bg, same px-2.5 buttons — so the three filter-style
 * pills sit in muscle memory across both surfaces.
 */
import { FilterSelect } from '@/components/primitives/FilterSelect';
import { cn } from '@/lib/utils';
import { CalendarBlank } from '@phosphor-icons/react';
import type { ReactElement } from 'react';

export type PageRange = 7 | 30 | 90 | 180 | 365;

/** Default look-back. Deliberately generous (6 months) — for RUM the
 *  pipes read the pre-aggregated daily MV so a wide window is cheap,
 *  and a fuller picture is more useful on first load than a thin 30d
 *  slice. Narrower windows stay one tap away. */
export const DEFAULT_PAGE_RANGE: PageRange = 180;

const RANGE_OPTIONS: ReadonlyArray<{ value: PageRange; label: string }> = [
  { value: 7, label: '7d' },
  { value: 30, label: '30d' },
  { value: 90, label: '90d' },
  { value: 180, label: '6mo' },
  { value: 365, label: '12mo' },
];

/** Dropdown form of the same control, for surfaces whose filters read
 *  as a row of `FilterSelect` pills (Performance › Real users). Labels
 *  spell the window out — as the trigger text they have to stand alone
 *  without the "7d / 30d" run of siblings for context. */
const RANGE_SELECT_OPTIONS = RANGE_OPTIONS.map((opt) => ({
  value: String(opt.value),
  label:
    opt.value < 180 ? `Last ${opt.value} days` : `Last ${opt.value === 180 ? '6' : '12'} months`,
}));

export function PageRangeSelect({
  value,
  onChange,
}: {
  value: PageRange;
  onChange: (next: PageRange) => void;
}): ReactElement {
  return (
    <FilterSelect
      ariaLabel="Date range"
      icon={<CalendarBlank aria-hidden="true" size={12} className="text-[var(--color-mute)]" />}
      value={String(value)}
      options={RANGE_SELECT_OPTIONS}
      onChange={(next) => onChange(Number(next) as PageRange)}
      // Always emphasised: unlike the audience filters there's no
      // "everything" default here — every view is a window, and which
      // one is on is the first thing you check when a number surprises.
      active
    />
  );
}

export function PageRangePill({
  value,
  onChange,
}: {
  value: PageRange;
  onChange: (next: PageRange) => void;
}): ReactElement {
  return (
    <div
      // biome-ignore lint/a11y/useSemanticElements: segmented control is a button row + role=group
      role="group"
      aria-label="Date range"
      className="inline-flex items-center gap-1 text-xs"
    >
      {RANGE_OPTIONS.map((opt) => {
        const active = opt.value === value;
        return (
          <button
            key={opt.value}
            type="button"
            aria-pressed={active}
            onClick={() => onChange(opt.value)}
            className={cn(
              'inline-flex items-center gap-1 rounded px-1 py-1 outline-none transition-colors',
              active
                ? 'bg-[var(--color-paper-alt)] font-semibold text-[var(--color-ink)]'
                : 'text-[var(--color-ink-soft)] hover:bg-[var(--color-paper-alt)]/60',
            )}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}
